import React, { useEffect, useState, useRef } from 'react';

import classNames from 'classnames';
import { Modal } from 'flowbite';

import SupportUserInvite from './SupportUserInvite';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { modalOptions } from 'Helpers/modalOptions';
import { combinePaginationUrl } from 'Helpers/urlService';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';

export default function SupportUsersTable(props) {
    const [roles, setRoles] = useState(props.roles);
    const [users, setUsers] = useState([]);
    const [organisationUsers, setOrganisationUsers] = useState([]);

    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [to, setTo] = useState(1);
    const [from, setFrom] = useState(1);
    const [total, setTotal] = useState(1);

    const [revokedUser, setRevokedUser] = useState(null);
    const [showToast, setShowToast] = useState(false);

    const [supportUserInviteModalElement, setSupportUserInviteModalElement] = useState(null);
    const [revokeAccessModalElement, setRevokeAccessModalElement] = useState(null);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const supportUserInviteModalRef = useRef();
    const revokeAccessModalRef = useRef();

    useEffect(() => {
        fetchUsers();
        fetchOrganisationUsers();
        if (props.canCreate) {
            supportUserInviteModalRef && setSupportUserInviteModalElement(new Modal(supportUserInviteModalRef.current, modalOptions(false)));
            revokeAccessModalRef && setRevokeAccessModalElement(new Modal(revokeAccessModalRef.current, modalOptions(false)));
        }
    }, []);

    useEffect(() => {
        if (revokedUser) {
            revokeAccessModalElement && revokeAccessModalElement.show();
        }
    }, [revokedUser]);

    const defaultUrl = combinePaginationUrl(props.usersUrl, perPage);
    const combineFetchUrl = (p = null, pp = null) => {
        return combinePaginationUrl(props.usersUrl, pp || perPage, p || page);
    };

    const fetchUsers = (url = null) => {
        const fetchUrl = url || defaultUrl;

        axios
            .get(fetchUrl)
            .then((res) => {
                const { data, to, from, total, last_page } = res.data;
                setUsers(data);
                setTo(to);
                setFrom(from);
                setTotal(total);
                setLastPage(last_page);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const fetchOrganisationUsers = () => {
        if (props.organisationSupportUsersUrl) {
            axios
                .get(props.organisationSupportUsersUrl)
                .then((res) => {
                    setOrganisationUsers(res.data.users);
                })
                .catch((e) => {
                    console.log(e);
                });
        }
    };

    function pageChange(data) {
        const url = combineFetchUrl(data.selected + 1);
        setPage(+data.selected + 1);
        fetchUsers(url);
    }

    const confirmRevokedUser = (user) => {
        setRevokedUser(user);
    };

    const clearRevokedUser = () => {
        setRevokedUser(null);
        revokeAccessModalElement && revokeAccessModalElement.hide();
    };

    const successRevokedHandler = () => {
        setShowToast(true);
        fetchOrganisationUsers();
        $('#user-revoked-toast').toast('show');
        setTimeout(() => {
            setShowToast(false);
        }, 3000);
    };

    const triggerSupportUserInviteModal = () => {
        supportUserInviteModalElement && supportUserInviteModalElement.show();
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerRevokeUser = () => {
        if (props.canCreate) {
            setActionInProgress(true);

            axios
                .delete(props.revokeUrl.replace('user_id', revokedUser.id))
                .then((response) => {
                    successRevokedHandler();
                    fetchUsers();
                    setActionCompleted(true);
                    clearRevokedUser();
                })
                .catch((e) => {
                    setActionInProgress(false);
                    alert(e.response.data.error);
                    console.log(e.response);
                });
        }
        return;
    };

    return (
        <>
            <div id="user-revoked-toast" className={'toast ' + !showToast ? 'hidden' : ''} style={{ position: 'absolute', top: 0, right: 0 }} data-delay={2000}>
                <div className="toast-header">
                    <strong className="mr-auto">{$t('Access revoked')}</strong>
                    <button type="button" className="ml-2 mb-1 close" data-dismiss="toast" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div className="toast-body">{$t('Access revoked successfully')}</div>
            </div>

            <div className="table__card">
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
                    <h4 className="table__header">{$t('Support Users')}</h4>
                    {!!props.canCreate && (
                        <Button variant="primary" onClick={() => triggerSupportUserInviteModal()}>
                            {$t('Invite Support User')}
                        </Button>
                    )}
                </div>

                <div className="inbud-table_wrapper">
                    <table className="inbud-table">
                        <thead>
                            <tr>
                                <th>{$t('Name')}</th>
                                <th>{$t('Email')}</th>
                                <th>{$t('Roles')}</th>
                                <th>{$t('Expire Time')}</th>
                                <th>{$t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => {
                                const modelUsersKey = props.type === 'organisation' ? 'organisation_users' : 'event_users';

                                return (
                                    <tr key={`support-${user.id}`} className={classNames({ 'tw-text-charcoal-50': user.disabled_at })}>
                                        <td>{user.first_name + ' ' + user.last_name}</td>
                                        <td>{user.email}</td>
                                        <td>
                                            {user[modelUsersKey][0].roles.length === 0
                                                ? $t('No assigned roles')
                                                : user[modelUsersKey][0].roles.map((elem) => elem.name).join(' | ')}
                                        </td>
                                        <td>{formatDateWithTz(user[modelUsersKey][0].access_to, 'YYYY-MM-DD HH:mm:ss', 'DD-MM-YYYY HH:mm')}</td>
                                        <td>
                                            {props.canCreate && (
                                                <Button variant="danger" onClick={() => confirmRevokedUser(user)}>
                                                    {$t('Revoke Access')}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {!!props.canCreate && (
                    <SupportUserInvite
                        modalId="invite-support-user"
                        modalRef={supportUserInviteModalRef}
                        modalElement={supportUserInviteModalElement}
                        type={props.type}
                        roles={roles}
                        canCreate={props.canCreate}
                        inviteUrl={props.inviteUrl}
                        fetchUsersHandler={fetchUsers}
                        organisationUsers={organisationUsers}
                    />
                )}

                {!!props.canCreate && (
                    <ConfirmationModal
                        successHandler={successRevokedHandler}
                        closeHandler={clearRevokedUser}
                        triggerAction={triggerRevokeUser}
                        cleanStates={cleanConfirmationModalStates}
                        allowAction={revokedUser != undefined}
                        modalId={'revoke-user'}
                        modalRef={revokeAccessModalRef}
                        modalElement={revokeAccessModalElement}
                        request={actionInProgress}
                        actionCompleted={actionCompleted}
                        actionFailed={actionFailed}
                        confirmationMsg={$t('Are you sure you want to revoke access to the {{model}} of the user {{name}}?', {
                            model: props.type === 'organisation' ? 'organisation' : 'event',
                            name: revokedUser?.first_name + ' ' + revokedUser?.last_name,
                        })}
                        actionCompletedMsg=""
                        actionFailedMsg=""
                        title={$t('Revoke Access')}
                        cancelButtonText={$t('Close')}
                        primaryButtonText={$t('Revoke Access')}
                    />
                )}
            </div>
        </>
    );
}
